import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import detectBrowserLanguage from 'detect-browser-language';
import { Skeleton } from 'primereact/skeleton';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import { LocationTaskLabel, LocationTaskTranslation } from './Types';

interface TranslateProps {
    locationTaskId: number;
    translationLabels: LocationTaskLabel;
    setTranslationLabels: React.Dispatch<React.SetStateAction<LocationTaskLabel>>;  // Parent component also needs to know the labels for translation so we use it's state instead of ours
    translation: LocationTaskTranslation | null;
    setTranslation: React.Dispatch<React.SetStateAction<LocationTaskTranslation | null>>;   // Parent component needs to render the translated task, so we use it's state instead of ours
    newDesign?: boolean;
}

const Translate = (props: TranslateProps): ReactElement => {

    const { locationTaskId, translationLabels, setTranslationLabels, translation, setTranslation, newDesign } = props;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userLanguage, setUserLanguage] = useState<string>('');
    const [canTranslate, setCanTranslate] = useState<boolean>(false);
    const [isTranslating, setIsTranslating] = useState<boolean>(false);

    const dispatch: Dispatch = useDispatch();
    const validateTranslation = async (id: number, language: string) => dispatch<any>(locationTaskActions.validateTranslation(id, language));
    const translateText = async (request) => dispatch<any>(locationTaskActions.translateText(request));

    const validateCanTranslate = async () => {
        const browserLanguage = detectBrowserLanguage();
        setUserLanguage(browserLanguage);

        try {
            const response = await validateTranslation(locationTaskId, browserLanguage);

            if (response.type == types.VALIDATE_TRANSLATION_TEXT_SUCCESS) {
                setCanTranslate(response.payload.canTranslate);
                setTranslationLabels(response.payload.labels as LocationTaskLabel);
            } else if (response.type == types.VALIDATE_TRANSLATION_TEXT_FAILURES) {
                setCanTranslate(false);
            }
        } catch (error) {
            setCanTranslate(false);
        }
    };

    const onLocationTaskTranslation = async () => {
        if (isTranslating)
            return;

        try {
            setIsTranslating(true);

            const request = {
                locationTaskId: locationTaskId,
                language: userLanguage,
            };
            const response = await translateText(request);

            if (response.type == types.TRANSLATE_TEXT_SUCCESS) {
                Notification('success', '', 'The location task was translated');
                setTranslation(response.payload.locationTask);
            } else if (response.type == types.TRANSLATE_TEXT_FAILURES) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsTranslating(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {

            setIsLoading(true);

            try {
                await validateCanTranslate();
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            } finally {
                setIsLoading(false);
            }
        };

        // Only load if we have a valid task ID (not in add mode)
        if (locationTaskId) {
            fetchData();
        }
    }, [locationTaskId]);

    return (
        <>
            {
                (newDesign ?
                    isLoading
                        ? <Skeleton width='15%' height='100%' className='' />
                        : !translation && canTranslate &&
                        <button
                            type='button'
                            title={translationLabels.translateLabel}
                            disabled={isTranslating}
                            onClick={onLocationTaskTranslation}
                            className={`footer-button ${isTranslating ? 'disabled' : ''}`}
                        >
                            <div className='centered'>
                                {isTranslating
                                    ? <i className={'far fa-gear fa-spin'}></i>
                                    : <i className={'far fa-globe'}></i>
                                }
                                <div>
                                    {translationLabels.translateLabel}
                                </div>
                            </div>
                        </button>
                    : isLoading
                        ? <Skeleton width='100%' height='100%' className='' />
                        : !translation && canTranslate &&
                        <div className='link-with-icon'>
                            {isTranslating
                                ? <i className={'far fa-gear fa-spin'}></i>
                                : <i className={'far fa-globe'}></i>
                            }
                            <button type='button' title={translationLabels.translateLabel} className={`btn btn-link ${isTranslating ? 'disabled' : ''}`} onClick={onLocationTaskTranslation}>
                                {translationLabels.translateLabel}
                            </button>
                        </div>
                )
            }
        </>
    );
};

export default Translate;
